import { useEffect, useRef, useState } from "react";
import { getPublicEmployerInviteCard, submitEmployerApplication } from "./publicEmployerInviteApi.js";
import {
  EMPLOYER_LANGUAGES,
  getPublicEmployerText,
  inferDefaultEmployerLanguage,
} from "./publicEmployerRegisterTranslations.js";
import "./publicInviteRegister.css";
import "./publicEmployerRegister.css";

/**
 * P156. The employer's own half of Agent -> Employer onboarding: the company
 * fills this in themselves from the Agent's invite link, and the moment they
 * submit, the application appears on that Agent's Org Chart as pending
 * ("เมื่อ submit จะเข้ามาในผังเลยครับ").
 *
 * Owner: "ให้เหมือนแรงงานเลยครับ มีจดหมาย แบบเดียวกับแรงงานเลย แค่เป็นฝั่ง
 * นายจ้างเท่านั้น" - so this is the exact three-step invite flow the worker
 * page uses (closed envelope -> letter -> form), reusing publicInviteRegister
 * .css's own wx-pr-* envelope/letter styling rather than a second copy of it.
 * Only the CONTENT differs: a company reads it, not a worker.
 *
 * P157-R2: and, like the worker letter, it is now language-aware. Every visible
 * string comes from publicEmployerRegisterTranslations; the page opens in the
 * language the Agent wrote the letter in (card.intendedLanguageCode) and the
 * company can switch between Thai and English with the topbar chips.
 *
 * Unauthenticated by design - the company has no account and onboarding does
 * not create one. Still its own page and its own API module: the owner's rule
 * is that the two sides never mix, and one shared "invite" page is how an
 * employer link would eventually render a worker form.
 */

// Matches the worker page exactly - a visual flourish only. The view switch is
// on a short timer and is skipped entirely under prefers-reduced-motion, so
// nothing about correctness depends on the number beyond "short".
const ENVELOPE_OPEN_ANIMATION_MS = 360;

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readInviteCodeFromUrl() {
  if (typeof window === "undefined") {
    return "";
  }
  return new URLSearchParams(window.location.search).get("invite")?.trim() ?? "";
}

function getInitials(name) {
  if (!name) {
    return "";
  }
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// The owner's own closed set - a preference the Agent reads, never a hard
// filter. Canonical (English) values are what the backend stores/validates
// (NationalityOptions.All); labels are the only part that's translated.
const NATIONALITY_OPTIONS = [
  { value: "Sri Lanka", labelKey: "nationalitySriLanka" },
  { value: "Bangladesh", labelKey: "nationalityBangladesh" },
  { value: "Laos", labelKey: "nationalityLaos" },
  { value: "Vietnam", labelKey: "nationalityVietnam" },
  { value: "Myanmar", labelKey: "nationalityMyanmar" },
];

const EMPTY_FORM = {
  legalName: "",
  displayName: "",
  taxId: "",
  primaryContactName: "",
  primaryPhoneNumber: "",
  addressText: "",
  desiredWorkerType: "",
  desiredWorkerCount: "",
  desiredNationalities: [],
};

export default function PublicEmployerRegisterPage() {
  const [inviteCode] = useState(readInviteCodeFromUrl);
  const [lookup, setLookup] = useState({ status: inviteCode ? "loading" : "missing-invite", data: null, error: null });
  const [view, setView] = useState("envelope"); // "envelope" | "letter" | "form"
  const [envelopeOpening, setEnvelopeOpening] = useState(false);
  const envelopeTimerRef = useRef(null);

  // Defaults to Thai and switches to the invite's own intended language the
  // moment the card loads - unless the company has already picked a chip
  // themselves (then their choice wins). Same rule as the worker page.
  const [language, setLanguageState] = useState("th");
  const languageTouchedRef = useRef(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [validationError, setValidationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successResponse, setSuccessResponse] = useState(null);

  const t = getPublicEmployerText(language);

  function handleLanguageChange(code) {
    languageTouchedRef.current = true;
    setLanguageState(code);
  }

  useEffect(() => {
    if (!inviteCode) {
      return undefined;
    }

    let cancelled = false;
    getPublicEmployerInviteCard(inviteCode)
      .then((data) => {
        if (!cancelled) {
          setLookup({ status: "ready", data, error: null });
          if (!languageTouchedRef.current) {
            const inferred = inferDefaultEmployerLanguage(data);
            if (inferred) {
              setLanguageState(inferred);
            }
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLookup({ status: "error", data: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  useEffect(
    () => () => {
      if (envelopeTimerRef.current) {
        window.clearTimeout(envelopeTimerRef.current);
      }
    },
    []
  );

  function handleOpenEnvelope() {
    if (prefersReducedMotion()) {
      setView("letter");
      return;
    }
    setEnvelopeOpening(true);
    envelopeTimerRef.current = window.setTimeout(() => setView("letter"), ENVELOPE_OPEN_ANIMATION_MS);
  }

  function updateField(field, value) {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  function toggleNationality(value) {
    setForm((previous) => {
      const has = previous.desiredNationalities.includes(value);
      return {
        ...previous,
        desiredNationalities: has
          ? previous.desiredNationalities.filter((item) => item !== value)
          : [...previous.desiredNationalities, value],
      };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();

    // Mirrors the backend's required set exactly - never stricter, so the form
    // cannot refuse something the API would have accepted.
    if (!form.legalName.trim()) {
      setValidationError(t.errorLegalNameRequired);
      return;
    }
    if (!form.primaryPhoneNumber.trim()) {
      setValidationError(t.errorPhoneRequired);
      return;
    }

    const trimmedCount = form.desiredWorkerCount.trim();
    let desiredWorkerCount;
    if (trimmedCount) {
      const parsed = Number(trimmedCount);
      if (!Number.isInteger(parsed) || parsed < 1) {
        setValidationError(t.errorWorkerCountInvalid);
        return;
      }
      desiredWorkerCount = parsed;
    }

    setValidationError(null);
    setSubmitting(true);
    try {
      const response = await submitEmployerApplication({
        inviteCode,
        legalName: form.legalName.trim(),
        displayName: form.displayName.trim() || undefined,
        taxId: form.taxId.trim() || undefined,
        primaryContactName: form.primaryContactName.trim() || undefined,
        primaryPhoneNumber: form.primaryPhoneNumber.trim(),
        addressText: form.addressText.trim() || undefined,
        desiredWorkerType: form.desiredWorkerType.trim() || undefined,
        desiredWorkerCount,
        desiredNationalities: form.desiredNationalities.length > 0 ? form.desiredNationalities.join(",") : undefined,
      });

      // A link that ran out between opening the letter and submitting comes
      // back as available:false, not as an error.
      if (response?.available === false) {
        setLookup((previous) => ({
          ...previous,
          data: { ...(previous.data ?? {}), available: false, unavailableReason: response.unavailableReason },
        }));
        return;
      }

      setSuccessResponse(response);
    } catch (error) {
      setValidationError(error?.message ?? t.submitGenericError);
    } finally {
      setSubmitting(false);
    }
  }

  const card = lookup.status === "ready" ? lookup.data : null;

  return (
    <div className="wx-pr-root">
      <Topbar t={t} appName={card?.appName} language={language} onLanguageChange={handleLanguageChange} />

      {lookup.status === "missing-invite" && <CenteredState title={t.missingInviteTitle} body={t.missingInviteBody} />}

      {lookup.status === "loading" && (
        <div className="wx-pr-center-wrap">
          <div className="wx-pr-spinner" aria-hidden="true" />
          <p className="wx-pr-loading-text">{t.loadingText}</p>
        </div>
      )}

      {lookup.status === "error" && (
        <CenteredState
          title={lookup.error?.status === 404 ? t.errorTitleNotFound : t.errorTitleGeneric}
          body={lookup.error?.message ?? t.errorBodyGeneric}
        />
      )}

      {card && !card.available && (
        <CenteredState
          title={t.unavailableTitle}
          /* The backend's reason is written for this reader - show it rather
             than replacing it with a generic sentence. */
          body={card.unavailableReason ?? t.unavailableDefault}
        />
      )}

      {card && card.available && successResponse && (
        <div className="wx-pr-center-wrap">
          <SuccessPanel t={t} response={successResponse} />
        </div>
      )}

      {card && card.available && !successResponse && view === "envelope" && (
        <main className="wx-pr-envelope-screen">
          <EnvelopePanel t={t} card={card} opening={envelopeOpening} onOpen={handleOpenEnvelope} />
        </main>
      )}

      {card && card.available && !successResponse && view === "letter" && (
        <main className="wx-pr-letter-screen">
          <LetterPanel t={t} card={card} onStart={() => setView("form")} />
        </main>
      )}

      {card && card.available && !successResponse && view === "form" && (
        <main className="wx-pr-form-screen">
          <FormPanel
            t={t}
            card={card}
            form={form}
            onChange={updateField}
            onToggleNationality={toggleNationality}
            onBack={() => setView("letter")}
            onSubmit={handleSubmit}
            submitting={submitting}
            validationError={validationError}
          />
        </main>
      )}
    </div>
  );
}

function Topbar({ t, appName, language, onLanguageChange }) {
  return (
    <header className="wx-per-topbar">
      <span className="wx-per-brand">{appName ?? "Workmate X"}</span>
      <div className="wx-per-topbar-right">
        <span className="wx-per-topbar-tag">{t.topbarTag}</span>
        <div className="wx-per-lang-group">
          {EMPLOYER_LANGUAGES.map((chip) => (
            <button
              key={chip.code}
              type="button"
              className={`wx-pr-lang-chip${language === chip.code ? " wx-pr-lang-chip--active" : ""}`}
              aria-pressed={language === chip.code}
              onClick={() => onLanguageChange(chip.code)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

function CenteredState({ title, body }) {
  return (
    <div className="wx-pr-center-wrap">
      <section className="wx-per-state-card">
        <h1>{title}</h1>
        <p>{body}</p>
      </section>
    </div>
  );
}

/** The closed envelope. Identical motion to the worker page - see its own note. */
function EnvelopePanel({ t, card, opening, onOpen }) {
  const initials = getInitials(card.agentDisplayName);
  const agentName = card.agentDisplayName ?? t.envelopeFallbackAgent;

  return (
    <section className={`wx-pr-envelope-card${opening ? " wx-pr-envelope-card--opening" : ""}`}>
      <div className="wx-pr-envelope-visual" aria-hidden="true">
        <div className="wx-pr-envelope-flap" />
        <div className="wx-pr-envelope-body">
          <div className="wx-pr-envelope-seal">{initials || "WX"}</div>
        </div>
      </div>
      <span className="wx-pr-envelope-eyebrow">{t.envelopeEyebrow}</span>
      <p className="wx-pr-envelope-from">{t.envelopeFrom(agentName)}</p>
      <p className="wx-pr-envelope-hint">{t.envelopeHint}</p>
      <button type="button" className="wx-pr-submit-button wx-pr-envelope-cta" onClick={onOpen}>
        {t.envelopeOpenCta}
      </button>
    </section>
  );
}

/** The letter itself - shown only after the envelope has been opened. */
function LetterPanel({ t, card, onStart }) {
  const agentName = card.agentDisplayName ?? t.envelopeFallbackAgent;
  const appName = card.appName ?? "Workmate X";

  return (
    <section className="wx-pr-letter-panel">
      <div className="wx-pr-letter-head">
        <span className="wx-pr-letter-eyebrow">{t.letterEyebrow}</span>
        <h1 className="wx-pr-letter-title">{t.letterTitle(agentName, appName)}</h1>
        <p className="wx-pr-letter-desc">{t.letterDesc}</p>
      </div>

      <div className="wx-pr-letter-explain">
        {card.noteToEmployer && <p className="wx-per-note">{card.noteToEmployer}</p>}
        <ul>
          {t.explainLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>

      <div className="wx-pr-letter-cta-bar">
        <button type="button" className="wx-pr-submit-button wx-pr-letter-cta-button" onClick={onStart}>
          {t.letterStartCta}
        </button>
      </div>
    </section>
  );
}

function FormPanel({ t, card, form, onChange, onToggleNationality, onBack, onSubmit, submitting, validationError }) {
  const agentName = card.agentDisplayName ?? t.envelopeFallbackAgent;

  return (
    <section className="wx-per-form-card">
      <div className="wx-per-form-head">
        <button type="button" className="wx-per-back" onClick={onBack}>
          {t.formBack}
        </button>
        <h1>{t.formHeading}</h1>
        <p className="wx-per-muted">{t.formInviteFrom(agentName)}</p>
      </div>

      <form className="wx-per-form" onSubmit={onSubmit}>
        <label className="wx-per-field">
          <span>
            {t.fieldLegalName} <b aria-hidden="true">*</b>
          </span>
          <input
            type="text"
            value={form.legalName}
            onChange={(event) => onChange("legalName", event.target.value)}
            placeholder={t.fieldLegalNamePlaceholder}
          />
        </label>

        <label className="wx-per-field">
          <span>{t.fieldDisplayName}</span>
          <input
            type="text"
            value={form.displayName}
            onChange={(event) => onChange("displayName", event.target.value)}
            placeholder={t.fieldDisplayNamePlaceholder}
          />
        </label>

        <label className="wx-per-field">
          <span>{t.fieldTaxId}</span>
          <input type="text" value={form.taxId} onChange={(event) => onChange("taxId", event.target.value)} />
        </label>

        <label className="wx-per-field">
          <span>{t.fieldContactName}</span>
          <input
            type="text"
            value={form.primaryContactName}
            onChange={(event) => onChange("primaryContactName", event.target.value)}
          />
        </label>

        <label className="wx-per-field">
          <span>
            {t.fieldPhone} <b aria-hidden="true">*</b>
          </span>
          <input
            type="tel"
            value={form.primaryPhoneNumber}
            onChange={(event) => onChange("primaryPhoneNumber", event.target.value)}
            placeholder={t.fieldPhonePlaceholder}
          />
        </label>

        <label className="wx-per-field">
          <span>{t.fieldAddress}</span>
          <textarea value={form.addressText} onChange={(event) => onChange("addressText", event.target.value)} rows={3} />
        </label>

        <div className="wx-per-field-divider" aria-hidden="true" />
        <p className="wx-per-section-heading">{t.workerNeedsHeading}</p>

        <label className="wx-per-field">
          <span>{t.fieldDesiredWorkerType}</span>
          <input
            type="text"
            value={form.desiredWorkerType}
            onChange={(event) => onChange("desiredWorkerType", event.target.value)}
            placeholder={t.fieldDesiredWorkerTypePlaceholder}
          />
        </label>

        <label className="wx-per-field">
          <span>{t.fieldDesiredWorkerCount}</span>
          <input
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={form.desiredWorkerCount}
            onChange={(event) => onChange("desiredWorkerCount", event.target.value)}
            placeholder={t.fieldDesiredWorkerCountPlaceholder}
          />
        </label>

        <div className="wx-per-field">
          <span>{t.fieldDesiredNationalities}</span>
          <div className="wx-per-nationality-group" role="group" aria-label={t.fieldDesiredNationalities}>
            {NATIONALITY_OPTIONS.map((option) => (
              <label key={option.value} className="wx-per-nationality-option">
                <input
                  type="checkbox"
                  checked={form.desiredNationalities.includes(option.value)}
                  onChange={() => onToggleNationality(option.value)}
                />
                <span>{t[option.labelKey]}</span>
              </label>
            ))}
          </div>
        </div>

        {validationError && (
          <p className="wx-per-error" role="alert">
            {validationError}
          </p>
        )}

        <button type="submit" className="wx-pr-submit-button" disabled={submitting}>
          {submitting ? t.submitButtonSubmitting : t.submitButton}
        </button>

        <p className="wx-per-muted wx-per-fineprint">{t.formFineprint}</p>
      </form>
    </section>
  );
}

function SuccessPanel({ t, response }) {
  const name = response?.agentDisplayName ?? t.successFallbackName;
  return (
    <section className="wx-per-state-card">
      <h1>{t.successTitle}</h1>
      <p>{response?.isDuplicate ? t.successDuplicateBody : t.successBody}</p>
      <p className="wx-per-muted">{t.successFollowup(name)}</p>
    </section>
  );
}
